import React, { useRef, useState } from "react";
import { getAttribution } from "@/lib/attribution";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api, { apiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { PageHero, Section } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const newMessageId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `cs-${Date.now()}-${Math.random().toString(16).slice(2)}`);

export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // Stable per-form-instance id: kept identical across retries so the CRM never duplicates.
  const messageIdRef = useRef(newMessageId());
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/public/contact", { ...form, preferred_language: lang, message_id: messageIdRef.current, ...getAttribution() });
      setSent(true);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
      messageIdRef.current = newMessageId();
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo pageKey="contact" />
      <PageHero kicker={t("contact.kicker")} title={t("contact.title")} lead={t("contact.lead")} />

      <Section>
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <form onSubmit={submit} data-testid="contact-form" className="md:col-span-7 space-y-6 border border-border bg-surface p-6 sm:p-10">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <Label htmlFor="contact-name">{t("contact.name")}</Label>
                <Input id="contact-name" data-testid="contact-name" required value={form.name} onChange={set("name")} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="contact-email">{t("contact.email")}</Label>
                <Input id="contact-email" data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} className="mt-2" />
              </div>
            </div>
            <div>
              <Label htmlFor="contact-subject">{t("contact.subject")}</Label>
              <Input id="contact-subject" data-testid="contact-subject" required value={form.subject} onChange={set("subject")} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="contact-message">{t("contact.message")}</Label>
              <Textarea id="contact-message" data-testid="contact-message" required rows={6} value={form.message} onChange={set("message")} className="mt-2" />
            </div>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="contact-honeypot"
                   value={form.website} onChange={set("website")} className="hidden" />
            <Button type="submit" disabled={sending} data-testid="contact-submit" className="rounded-full bg-accent px-8 text-accent-foreground hover:bg-accent/90">
              {sending ? t("common.loading") : t("contact.send")}
            </Button>
            {sent && (
              <p data-testid="contact-success" className="text-sm text-accent">
                {t("contact.success")}
              </p>
            )}
          </form>
          <aside className="md:col-span-5">
            <div className="border border-border bg-secondary/40 p-8">
              <p className="cs-kicker">{t("common.brand")}</p>
              <p className="mt-4 font-display text-2xl">{t("footer.tagline")}</p>
              <p data-testid="contact-address" className="mt-6 whitespace-pre-line text-sm text-muted-foreground">
                <span className="cs-kicker block text-muted-foreground/70">{t("footer.addressLabel")}</span>
                {t("footer.address")}
              </p>
              <p className="mt-6 text-sm text-muted-foreground">{t("footer.confidentiality")}</p>
            </div>
          </aside>
        </div>
      </Section>
    </div>
  );
}
